import { JobType, ResumeDocument } from '../../types';
import { canonicalSkill, expandSkillTerms } from './skills';

export interface FitResult {
  // 0–100: share of the resume's skills found in the job.
  score: number;
  // Original-cased resume skills that matched (for a transparent chip).
  matched: string[];
}

const normalize = (s: string): string => s.toLowerCase().trim();

// Strip HTML tags + entities and collapse whitespace so a job's free-text
// description can be scanned the same way as its tags. Lightweight on
// purpose (mirrors the stripping used in Description.tsx) — no DOM parser.
const stripHtml = (html: string): string =>
  html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&[a-z]+;/gi, ' ')
    .replace(/\s+/g, ' ');

const escapeRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// A skill matches a job when it (or any of its known aliases — "JS" ↔
// "JavaScript") equals one of the job's tags (exact, case-insensitive) or
// appears as a whole token in the job's text. The token boundaries
// ([^a-z0-9] / start / end) stop "Java" from matching inside "JavaScript"
// while still allowing "C++", "react.js", "Node.js", etc.
const skillMatches = (skill: string, tagSet: Set<string>, text: string): boolean => {
  for (const term of expandSkillTerms(skill)) {
    if (!term) continue;
    if (tagSet.has(term)) return true;
    const re = new RegExp(`(^|[^a-z0-9])${escapeRegExp(term)}([^a-z0-9]|$)`, 'i');
    if (re.test(text)) return true;
  }
  return false;
};

// Build the searchable text for a job once (position + tags + description).
// Description is capped so a handful of very long postings can't dominate the
// cost when scoring thousands of jobs.
const jobText = (job: JobType): string =>
  normalize(
    [
      job.position ?? '',
      (job.tags ?? []).join(' '),
      stripHtml((job.description ?? '').slice(0, 3000)),
    ].join(' ')
  );

export const computeFitScore = (
  resume: ResumeDocument | null | undefined,
  job: JobType
): FitResult => {
  // De-dupe by canonical skill (so "JS" and "JavaScript" count once) but keep
  // each skill's first original casing (the chip reads "React", not "react").
  const seen = new Set<string>();
  const skills: string[] = [];
  for (const raw of resume?.skills ?? []) {
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const key = canonicalSkill(trimmed);
    if (seen.has(key)) continue;
    seen.add(key);
    skills.push(trimmed);
  }
  if (skills.length === 0) return { score: 0, matched: [] };

  const tagSet = new Set((job.tags ?? []).map(normalize).filter(Boolean));
  const text = jobText(job);

  const matched = skills.filter((skill) => skillMatches(skill, tagSet, text));
  const score = Math.round((matched.length / skills.length) * 100);
  return { score, matched };
};

// Pick which resume to match jobs against. Prefer the explicitly chosen
// `activeResumeId`; otherwise the most recently updated resume. Returns null
// when the user has no resumes (so callers can hide the fit UI).
export const getPrimaryResume = (
  resumes: ResumeDocument[],
  activeResumeId?: string
): ResumeDocument | null => {
  if (!resumes || resumes.length === 0) return null;
  if (activeResumeId) {
    const chosen = resumes.find((r) => r.id === activeResumeId);
    if (chosen) return chosen;
  }
  // NaN-safe: a missing/unparseable updatedAt counts as oldest, so a valid
  // newer resume is never beaten by one with a blank timestamp.
  return resumes.reduce((best, r) => {
    const tr = Date.parse(r.updatedAt);
    const tb = Date.parse(best.updatedAt);
    const rNewer = !Number.isNaN(tr) && (Number.isNaN(tb) || tr > tb);
    return rNewer ? r : best;
  });
};

// Add dummy default export for Expo Router (every file under app/ needs one).
export default { computeFitScore, getPrimaryResume };
